import axios from 'axios'
import {ID} from '../../../../../_metronic/helpers'
import {Category, Doc, DocComment} from './_models'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'
const API_URL = `${BASE_URL}/doc`
const CATEGORY_URL = `${BASE_URL}/category`
const COMMENT_URL = `${BASE_URL}/comment`

// Global helper to handle both intercepted and non-intercepted responses
const handleResponse = (res: any) => {
  const data =
    res?.data?.data !== undefined ? res.data.data : res?.data !== undefined ? res.data : res
  return data?.entities !== undefined ? data.entities : data
}

// Docs
const getDocs = async (): Promise<Doc[]> => {
  const res = await axios.get(API_URL)
  const data = handleResponse(res)
  if (!Array.isArray(data)) return []
  return data.map((doc: any) => ({
    ...doc,
    shortDesc: doc.short_desc || doc.shortDesc,
  }))
}

const getDocById = async (id: ID): Promise<Doc | undefined> => {
  const res = await axios.get(`${API_URL}/${id}`)
  const data = handleResponse(res)
  if (!data) return undefined
  return {
    ...data,
    shortDesc: data.short_desc || data.shortDesc,
  }
}

const createDoc = async (doc: Record<string, unknown>): Promise<Doc> => {
  const res = await axios.post(API_URL, doc)
  return handleResponse(res)
}

const updateDoc = async (id: ID, doc: Record<string, unknown>): Promise<Doc> => {
  const res = await axios.put(`${API_URL}/${id}`, doc)
  return handleResponse(res)
}

const deleteDoc = async (id: ID): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`)
}

const toggleDocPublish = async (id: ID, currentStatus: 'PUBLISHED' | 'DRAFT'): Promise<Doc> => {
  const newStatus = currentStatus === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED'
  const res = await axios.patch(`${API_URL}/${id}`, { status: newStatus })
  return handleResponse(res)
}

const toggleDocFeatured = async (id: ID, featured: boolean): Promise<Doc> => {
  const res = await axios.patch(`${API_URL}/${id}/featured`, { featured })
  return handleResponse(res)
}

// Categories
const getCategories = async (): Promise<Category[]> => {
  const res = await axios.get(CATEGORY_URL, {params: {filter: JSON.stringify({type: 'docs'})}})
  return handleResponse(res)
}

const createCategory = async (category: Partial<Category>): Promise<Category> => {
  const res = await axios.post(CATEGORY_URL, {...category, type: 'docs'})
  return handleResponse(res)
}

const updateCategory = async (id: ID, category: Partial<Category>): Promise<Category> => {
  const res = await axios.put(`${CATEGORY_URL}/${id}`, category)
  return handleResponse(res)
}

const deleteCategory = async (id: ID): Promise<void> => {
  await axios.delete(`${CATEGORY_URL}/${id}`)
}

// Comments
const getDocComments = async (docId?: ID): Promise<DocComment[]> => {
  if (!docId) return []
  const filter = {doc_id: docId}
  const res = await axios.get(COMMENT_URL, {
    params: {filter: JSON.stringify(filter)},
  })
  const response = handleResponse(res)
  const items = (response?.entities || response) ?? []
  if (!Array.isArray(items)) return []
  return items.filter((comment: any) => {
    const commentDocId = comment.doc_id ?? comment.DocId ?? comment.doc?.id
    return commentDocId === undefined || commentDocId === null || String(commentDocId) === String(docId)
  })
}

const updateDocCommentStatus = async (
  id: ID,
  status: 'PUBLISHED' | 'DRAFT'
): Promise<DocComment> => {
  const res = await axios.patch(`${COMMENT_URL}/${id}`, {status})
  return handleResponse(res)
}

const deleteDocComment = async (id: ID | string): Promise<void> => {
  await axios.delete(`${COMMENT_URL}/${id}`)
}

export {
  getDocs,
  getDocById,
  createDoc,
  updateDoc,
  deleteDoc,
  toggleDocPublish,
  toggleDocFeatured,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getDocComments,
  updateDocCommentStatus,
  deleteDocComment,
}
